import Cookies from 'js-cookie';
import { createBrowserStore } from './createBrowserStore';

const safeJsonParse = (data: any) => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (e) {
      if (data) return data;
      return undefined;
    }
  }
  return undefined;
};

const prepareValueToStore = (value: any) => {
  if (typeof value === 'object' && !Array.isArray(value)) {
    return JSON.stringify(value);
  }
  return value;
};

/*
 * evaluateHostname return information on the current hostname
 * if internal (tailoor.com) then the domain property is the hostname (i.e: sunflower.tailoor.com)
 * if on a custom domain then the `domain` property is the first level domain (i.e: lanieri.com)
 *
 * The `domain` property returned is used to set cookies options so that cookies for customers on same first level domain
 * would not collide with each other.
 */
export const evaluateHostname = (hostname: string) => {
  if (hostname.length < 2) {
    throw new Error('hostname length less then 2');
  }

  const domain = hostname.split('.').slice(-2).join('.');
  const isInternal = domain === 'tailoor.com' || domain === 'laike.me';
  const isOnlyTailoorInternal = domain === 'tailoor.com';

  if (isInternal) {
    return {
      domain: hostname,
      isInternal,
      isOnlyTailoorInternal
    } as const;
  }
  return {
    domain,
    isInternal,
    isOnlyTailoorInternal
  } as const;
};

const { domain } = evaluateHostname(window.location.hostname);

export const defaultCookieOptions: Cookies.CookieAttributes = {
  domain,
  secure: true,
  sameSite: 'none',
  path: '/',
};

export function setCookieExpiration(
  opt: Cookies.CookieAttributes,
  timestamp?: number
): Cookies.CookieAttributes {
  if (!timestamp) {
    return opt;
  }
  const expiration = new Date(timestamp * 1000);
  return {
    ...opt,
    expires: expiration,
  };
}

export const CookieStore = createBrowserStore({
  name: 'cookies',
  options: { withCache: false },
  get: (key: string) => {
    try {
      const item = Cookies.get(key);
      return safeJsonParse(item);
    } catch (error) {
      return undefined;
    }
  },
  set: (key, value, opts) => {
    const valueToStore = prepareValueToStore(value);
    const options: Cookies.CookieAttributes = opts ?? defaultCookieOptions;
    Cookies.set(key, valueToStore, options);
  },
  delete: (key, opts) => {
    const options: Cookies.CookieAttributes = opts ?? defaultCookieOptions;
    Cookies.remove(key, options);
  },
});

export const LocalStorageStore = createBrowserStore({
  name: 'localStorage',
  get: (key) => {
    const item = window.localStorage.getItem(key);
    return safeJsonParse(item);
  },
  set: (key, value) => {
    const valueToStore = prepareValueToStore(value);
    window.localStorage.setItem(key, valueToStore);
  },
  delete: (key) => {
    window.localStorage.removeItem(key);
  },
});

export const SessionStorageStore = createBrowserStore({
  name: 'sessionStorage',
  get: (key) => {
    const item = window.sessionStorage.getItem(key);
    return safeJsonParse(item);
  },
  set: (key, value) => {
    const newValue = prepareValueToStore(value);
    window.sessionStorage.setItem(key, newValue);
  },
  delete: (key) => {
    window.sessionStorage.removeItem(key);
  },
});
